// Shared register logic for the Meter Read App.
// `ETNAccount` / `ETNReading` are ambient global types (see global.d.ts).

const DEFAULT_REGISTER = '1';

export interface RegisterRow {
  value: string;
  label: string;
  done: boolean;
}

/** Display label for a register id, matching the submit screen's picker. */
export function registerLabel(value: string): string {
  return value === DEFAULT_REGISTER ? '1 (Default)' : value;
}

/**
 * Full set of registers a meter has, as shown to the user: the implicit default
 * register '1' followed by any additional registerIds, de-duplicated (some meters
 * erroneously list '1' explicitly alongside their real registers).
 */
export function getRegisterValues(account: Pick<ETNAccount, 'registerIds'>): string[] {
  const ids = account.registerIds || [];
  return Array.from(new Set([DEFAULT_REGISTER, ...ids]));
}

/** True when a meter has more than one register, so a single read is not enough. */
export function isMultiRegister(account: Pick<ETNAccount, 'registerIds'>): boolean {
  const ids = account.registerIds || [];
  return ids.length > 1 || (ids.length === 1 && ids[0] !== DEFAULT_REGISTER);
}

/** One calendar month before `now` (variable length, native, handles year rollover). */
export function oneMonthAgo(now: Date = new Date()): Date {
  const cutoff = new Date(now);
  const day = cutoff.getDate();
  cutoff.setMonth(cutoff.getMonth() - 1);
  if (cutoff.getDate() !== day) {
    // setMonth overflowed into the wrong month (e.g. "Feb 31" -> Mar 3);
    // clamp back to the last day of the intended month so the window doesn't shrink.
    cutoff.setDate(0);
  }
  return cutoff;
}

/** Build checklist rows: every register on the meter, marked done if recently read. */
export function buildRegisterRows(
  registerValues: string[],
  doneRegisterIds: string[]
): RegisterRow[] {
  const done = new Set(doneRegisterIds);
  return registerValues.map((value) => ({
    value,
    label: registerLabel(value),
    done: done.has(value)
  }));
}

interface QueueLike {
  status: string;
  state: {
    selectedAccount?: { _id: string };
    accountId?: string;
    reading: { registerId: string; submittedAt: string | Date };
  };
}

/**
 * Register ids submitted for this account within the window, from the persisted
 * offline queue. Excludes failed items; treats a missing registerId as the default.
 */
export function recentRegisterIdsFromQueue(
  queue: QueueLike[],
  accountId: string,
  cutoff: Date
): string[] {
  return queue
    .filter((item) => item.status !== 'error')
    .filter((item) => (item.state.selectedAccount?._id ?? item.state.accountId) === accountId)
    .filter((item) => new Date(item.state.reading.submittedAt) >= cutoff)
    .map((item) => item.state.reading.registerId || DEFAULT_REGISTER);
}
